import React, { useId, useState } from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import {
  AccountBalanceRounded,
  EmojiEventsRounded,
  ReceiptLongRounded,
} from "@mui/icons-material";
import { Check, ChevronDown } from "lucide-react";
import { useMembership } from "../../contexts/MembershipContext";

export type SuperInvestorTab = "super-investors" | "congressional" | "trades";
const sections = [
  {
    value: "super-investors",
    label: "Super Investors",
    icon: EmojiEventsRounded,
  },
  {
    value: "congressional",
    label: "Congressional Portfolios",
    icon: AccountBalanceRounded,
  },
  {
    value: "trades",
    label: "Latest Trade Disclosures",
    icon: ReceiptLongRounded,
  },
] as const;

interface SuperInvestorTabNavigationProps {
  activeTab: SuperInvestorTab;
  onTabChange: (tab: SuperInvestorTab) => void;
}
const SuperInvestorTabNavigation = ({
  activeTab,
  onTabChange,
}: SuperInvestorTabNavigationProps) => {
  const { isMembershipActive } = useMembership();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  const current =
    sections.find((section) => section.value === activeTab) ?? sections[0];
  const Icon = current.icon;
  return (
    <>
      <Button
        id={`${id}-trigger`}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-controls={anchor ? id : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<Icon sx={{ color: "primary.main", fontSize: 20 }} />}
        endIcon={<ChevronDown size={16} />}
        sx={{
          minHeight: 32,
          py: 0.5,
          lineHeight: 1.4,
          "@media (pointer: coarse)": { minHeight: 44 },
          bgcolor: "background.surface",
          border: 1,
          borderColor: "divider",
          maxWidth: "100%",
          px: 1.25,
          gap: 0.5,
          color: "text.primary",
          fontSize: "0.875rem",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: 1,
          "& .MuiButton-endIcon": { color: "text.secondary", ml: 1 },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {current.label}
        </Box>
      </Button>
      <Menu
        id={id}
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        MenuListProps={{ "aria-labelledby": `${id}-trigger` }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              p: 0.5,
              borderRadius: 1.5,
              bgcolor: "background.surface",
              border: 1,
              borderColor: "divider",
              boxShadow: "none",
              backgroundImage: "none",
              width: 300,
              maxWidth: "calc(100vw - 32px)",
            },
          },
        }}
      >
        {sections.map(({ icon: SectionIcon, ...section }) => (
          <MenuItem
            key={section.value}
            selected={section.value === activeTab}
            disabled={
              section.value !== "super-investors" && !isMembershipActive
            }
            onClick={() => {
              setAnchor(null);
              onTabChange(section.value);
            }}
            sx={{
              gap: 1.5,
              py: 0.875,
              px: 1.5,
              borderRadius: 1,
              "&.Mui-selected": {
                bgcolor: "transparent",
                color: "primary.main",
              },
              "&.Mui-selected:hover": { bgcolor: "action.hover" },
              whiteSpace: "normal",
            }}
          >
            <SectionIcon
              sx={{ fontSize: 20, color: "primary.main", flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color:
                    section.value === activeTab
                      ? "primary.main"
                      : "text.primary",
                }}
              >
                {section.label}
              </Typography>
            </Box>
            {section.value === activeTab && (
              <Check size={16} aria-hidden style={{ flexShrink: 0 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
export default SuperInvestorTabNavigation;
